import { ResourceProps } from "@refinedev/core";
import {
  IconAppsFilled,
  IconArticleFilled,
  IconCreditCardFilled,
  IconFolderFilled,
  IconTriangleSquareCircleFilled,
  IconUserFilled,
} from "@tabler/icons-react";
import { IconAdd } from "json-edit-react";
import { IconPlus } from "@tabler/icons";

export enum Resources {
  Companies = "companies",
  Resources = "resources",
  Services = "services",
  GroupedServices = "groupedServices",
  GroupedResources = "groupedResources",
  EntityGroups = "entityGroups",
  EntityGroupBulk = "entityGroupsBulk",
  ServiceResourceLinks = "links",
  ResourceServiceLinks = "resourceLinks",
  Prices = "prices",
  Discounts = "discounts",
  Tickets = "tickets",
  WorkingHours = "workingHours",
  WorkingHoursRange = "workingHoursRange",
  ClosingPeriods = "closingPeriods",
  ClosingPeriodsRange = "closingPeriodsRange",
  ServiceSettings = "serviceSettings",
  Clients = "clients",
  Capacity = "capacity",
  ResourceCapacity = "capacity-rules",
  ICalFeed = "icalFeed",
  TicketAvailability = "ticket-availability-rules",
  Times = "times",
  Bookings = "bookings",
  Slots = "slots",
  ScheduleSlots = "scheduleSlots",
  Developers = "developers",
  APIKeys = "apiKeys",
  Marketplace = "marketplace",
  Plugins = "plugins",
  PaymentPreferredProvider = "paymentPreferredProvider",
  WebHooks = "webhooks",
  WebHookSubscriptions = "webhookSubscriptions",
  Plan = "plan",
  PlanCheckoutURL = "planCheckoutURL",
  BillingURL = "billingURL",
  CompanyInvite = "companyInvite",
  CompanyInviteAccept = "companyInviteAccept",
  CompanyAccess = "companyAccess",
  CompanyAccessRights = "companyAccessRights",
  Settings = "settings",
  EnabledPlugins = "enabledPlugins",

  //plugins
  Klix = "klix",
  Stripe = "stripe",
  Decta = "decta",
  MercadoPago = "mercadopago",
  Montonio = "montonio",
  PaymentSettingsPatch = "paymentSettingsPatch",
  Subscriptions = "subscription",
  SubscriptionPrices = "subscription-prices",
  SubscriptionCapacity = "subscription-capacity",
  SubscriptionSettings = "subscription-settings",
  SubscriptionContracts = "subscription-contracts",
  ContractVisitLogs = "contract-visit-logs",
  Emails = "emails",
  EmailSettings = "email-settings",
  EmailSender = "email-sender",
  EmailSenderResend = "email-sender-resend",
  EmailSenderSendTest = "email-sender-send-test",
  EmailReminders = "email-reminders",
  GoogleCalendar = "google-calendar",
  GoogleCalendarAuth = "google-calendar-auth",
  GoogleCalendarCalendars = "google-calendar-calendars",
  Outseta = "outseta",
  GiftCards = "giftcards",
  GiftCardPurchases = "giftcard-purchases",
  GiftCardUsages = "giftcard-usages",
  PromoCodes = "promocodes",
  PromoCodeUsages = "promocode-usages",
  Payments = "payments",
  Refunds = "refunds",
  CompanyRefunds = "company-refunds",
  Addons = "addons",
  AddonsGroup = "addons-group",
  AddonsServiceLinks = "addons-service-links",
  AddonsSubscriptionLinks = "addons-subscription-links",
  PDFTemplates = "pdf-templates",
  CancellationRules = "bookingrules/cancellation/list",
  ReschedulingRules = "bookingrules/rescheduling/list",
  ContactDetails = "bookingrules/contacts",
  CodeValidate = "code-validate",
  ActivityLogs = "activity-logs",
  BookingResendEmail = "booking-resend-email",
}

export const resources: ResourceProps[] = [
  {
    name: Resources.Companies,
    list: "/companies",
    create: "/companies/create",
    edit: "/companies/edit/:id",
    show: "/companies/show/:id",
    meta: {
      canDelete: true,
    },
  },
  {
    name: Resources.Resources,
    list: "/companies/:company_id/resources",
    create: "/companies/:company_id/resources/create",
    edit: "/companies/:company_id/resources/edit/:id",
    show: "/companies/:company_id/resources/show/:id",
    meta: {
      canDelete: true,
      label: "Resources",
      icon: <IconFolderFilled size={16} />,
    },
  },
  {
    name: Resources.Services,
    list: "/companies/:company_id/services",
    create: "/companies/:company_id/services/create",
    edit: "/companies/:company_id/services/edit/:id",
    show: "/companies/:company_id/services/show/:id",
    meta: {
      canDelete: true,
      label: "Services",
      icon: <IconTriangleSquareCircleFilled size={16} />,
    },
  },
  {
    name: Resources.Clients,
    list: "/companies/:company_id/clients",
    create: "/companies/:company_id/clients/create",
    edit: "/companies/:company_id/clients/edit/:id",
    show: "/companies/:company_id/clients/show/:id",
    meta: {
      canDelete: true,
      label: "Clients",
      icon: <IconUserFilled size={16} />,
    },
  },
  {
    name: Resources.Bookings,
    list: "/companies/:company_id/bookings",
    create: "/companies/:company_id/bookings/create",
    edit: "/companies/:company_id/bookings/edit/:id",
    show: "/companies/:company_id/bookings/show/:id",
    meta: {
      canDelete: true,
      label: "Bookings",
      icon: <IconArticleFilled size={16} />,
    },
  },
  {
    name: Resources.Payments,
    list: "/companies/:company_id/payments",
    meta: {
      label: "Payments",
      icon: <IconCreditCardFilled size={16} />,
    },
  },
  {
    name: Resources.Plugins,
    list: "/companies/:company_id/plugins",
    meta: {
      label: "Plugins",
      icon: <IconAppsFilled size={16} />,
    },
  },
  {
    name: Resources.Developers,
    show: "/companies/:company_id/developers",
  },
  {
    name: Resources.Plan,
    list: "/plans",
  },
  {
    name: Resources.Settings,
    show: "/companies/:company_id/settings",
  },
  {
    name: Resources.Klix,
    show: "/companies/:company_id/plugins/klix",
  },
  {
    name: Resources.Stripe,
    list: "/companies/:company_id/plugins/stripe",
  },
  {
    name: Resources.Decta,
    list: "/companies/:company_id/plugins/decta",
  },
  {
    name: Resources.MercadoPago,
    list: "/companies/:company_id/plugins/mercadopago",
  },
  {
    name: Resources.Montonio,
    list: "/companies/:company_id/plugins/montonio",
  },
  {
    name: Resources.Subscriptions,
    list: "/companies/:company_id/plugins/subscription",
    create: "/companies/:company_id/plugins/subscription/create",
  },
  {
    name: Resources.GiftCards,
    list: "/companies/:company_id/plugins/giftcards",
    create: "/companies/:company_id/plugins/giftcards/create",
  },
  {
    name: Resources.PromoCodes,
    list: "/companies/:company_id/plugins/promocodes",
    create: "/companies/:company_id/plugins/promocodes/create",
  },
  {
    name: Resources.Emails,
    list: "/companies/:company_id/plugins/emails",
  },
  {
    name: Resources.Outseta,
    list: "/companies/:company_id/plugins/outseta",
  },
  {
    name: Resources.Addons,
    list: "/companies/:company_id/plugins/addons",
  },
  {
    name: Resources.GoogleCalendar,
    list: "/companies/:company_id/plugins/google-calendar",
  },
  {
    name: Resources.Marketplace,
    list: "/companies/:company_id/plugins/marketplace",
    meta: {
      label: "Add plugin",
      icon: <IconPlus size={16} />,
      parent: Resources.Plugins,
    },
  },
  {
    name: Resources.CancellationRules,
    list: "/companies/:company_id/plugins/bookingrules",
    create: "/companies/:company_id/plugins/bookingrules/create-cancellation",
  },
  {
    name: Resources.ReschedulingRules,
    list: "/companies/:company_id/plugins/bookingrules",
    create: "/companies/:company_id/plugins/bookingrules/create-rescheduling",
  },
];
